import React, { useEffect, useState } from 'react';
import {
	TextField,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Button,
} from '@material-ui/core';
import Loader from 'react-loader-spinner';
import { MainTemplate } from 'shared/MainTemplate/MainTemplate';
import handleNotification from 'helpers/handleNotification';
import {
	componentsServices,
	templateServices,
	taxonomyServices,
} from 'api/services';
import { IResTaxonomy } from 'api/services/taxonomies/types';
import { InputAutoSize } from 'shared/InputAutoSize/InputAutoSize';
import { IResComponent } from 'api/services/components/types';
import { ITemplate, TemplateTypes } from 'api/services/templates/types';
import templateTypes from 'constants/templateTypes';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { AppState } from 'store/store';
import redirect from 'helpers/redirect';
import './CreateTemplatePage.sass';

export const CreateTemplatePage: React.FC = () => {
	const [title, setTitle] = useState('');
	const [name, setName] = useState('');
	const [templateData, setTemplateData] = useState('');
	const [templateView, setTemplateView] = useState('');
	const [selectedTaxonomies, setSelectedTaxonomies] = useState<number[]>([]);
	const [taxonomies, setTaxonomies] = useState<IResTaxonomy[]>();
	const [loading, setLoading] = useState(false);
	const [templateType, setTemplateType] = useState<TemplateTypes>();
	const [hasError, setHasError] = useState(false);
	const [components, setComponents] = useState<IResComponent[]>();
	const [componentIds, setComponentIds] = useState<number[]>([]);
	const { user } = useSelector((state: AppState) => state.userReducer);
	const { currentLang } = useSelector((state: AppState) => state.langReducer);
	const history = useHistory();

	useEffect(() => {
		if (user) redirect(user?.role);
	}, [user?.role]);

	useEffect(() => {
		if (currentLang?.code) {
			getComponents();
			getTaxonomies();
		}
	}, [currentLang?.code]);

	const getComponents = async () => {
		const params = {};
		const res = await componentsServices
			.getComponents(params)
			.then((res) => res.data);
		setComponents(res.data);
	};

	const getTaxonomies = async () => {
		const res = await taxonomyServices.getTaxonomies().then((res) => res.data);
		setTaxonomies(res);
	};

	const createTemplate = async (e: React.FormEvent) => {
		e.preventDefault();
		if (
			!title.trim() ||
			!name.trim() ||
			!componentIds ||
			!selectedTaxonomies ||
			!templateType
		) {
			handleNotification('Заполните поля');
			setHasError(true);
			return;
		}
		setLoading(true);
		let data: ITemplate;
		try {
			data = {
				title,
				name,
				...(templateData
					? { properties: JSON.parse(templateData || '{}') }
					: {}),
				...(templateView ? { view: JSON.parse(templateView || '[]') } : {}),
				...(templateType === 'POST' ? { taxonomyIds: selectedTaxonomies } : {}),
				type: templateType as TemplateTypes,
				components: componentIds,
			};
		} catch {
			handleNotification('Не верный формат JSON');
			return;
		}
		try {
			await templateServices.createTemplate(data);
			handleNotification('Шаблон сохранен успешно', 'success');
			history.push('/templates');
		} catch (e: any) {
			handleNotification(
				e?.resposne?.data?.message || 'Произошла ошибка попробуйте позже',
			);
			setLoading(false);
		}
	};

	return (
		<MainTemplate title='Создание шаблона' hideBtn>
			<form onSubmit={createTemplate}>
				<TextField
					variant='outlined'
					label='Название'
					className='form-field'
					value={name}
					error={hasError && !name.trim()}
					onChange={(e) => setName(e.target.value)}
				/>
				<TextField
					variant='outlined'
					label='Заголовок шаблона'
					className='form-field'
					value={title}
					error={hasError && !title.trim()}
					onChange={(e) => setTitle(e.target.value)}
				/>
				<FormControl variant='outlined' className='form-field'>
					<InputLabel id='categories'>Компоненты</InputLabel>
					<Select
						label='Компоненты'
						value={componentIds}
						multiple
						onChange={(e) => setComponentIds(e.target.value as number[])}>
						{components?.map((i) => {
							return (
								<MenuItem key={i.id} value={i.id}>
									{i.title}
								</MenuItem>
							);
						})}
					</Select>
				</FormControl>
				<FormControl
					className='form-field'
					variant='outlined'
					error={hasError && !templateType}>
					<InputLabel>Тип</InputLabel>
					<Select
						label='Тип'
						value={templateType}
						onChange={(e) => setTemplateType(e.target.value as TemplateTypes)}>
						{templateTypes?.map((i) => {
							return (
								<MenuItem key={i.value} value={i.value}>
									{i.label}
								</MenuItem>
							);
						})}
					</Select>
				</FormControl>
				{templateType === 'POST' && (
					<FormControl className='form-field' variant='outlined'>
						<InputLabel id='categories'>Таксономии</InputLabel>
						<Select
							labelId='categories'
							label='Таксономии'
							multiple
							value={selectedTaxonomies}
							onChange={(e) =>
								setSelectedTaxonomies(e.target.value as number[])
							}>
							{taxonomies?.map((i) => {
								return (
									<MenuItem key={i.id} value={i.id}>
										{i.name}
									</MenuItem>
								);
							})}
						</Select>
					</FormControl>
				)}
				<InputAutoSize
					value={templateData}
					placeholder='Данные шаблона'
					updateValue={setTemplateData}
				/>
				<InputAutoSize
					value={templateView}
					placeholder='Настройка отображения'
					updateValue={setTemplateView}
				/>
				<Button
					className='action-button'
					size='large'
					color='primary'
					type='submit'
					variant='contained'
					disabled={loading}>
					{loading ? (
						<Loader height={10} type='ThreeDots' color='#fff' />
					) : (
						'Создать шаблон'
					)}
				</Button>
			</form>
		</MainTemplate>
	);
};
